/**
 * mapRateLimited
 * @description Runs `fn` over `items` with BOTH a bounded concurrency and a minimum interval between
 * task starts, so the effective request rate stays under an external limiter (e.g. nginx `limit_req`).
 *
 * Why not `Promise.all(items.map(fn))`: firing all N requests at once bursts past the nginx `/v1`
 * rate-limit (rate 60 r/s, burst 40) → 429 storm. Why not a plain concurrency pool: concurrency ÷
 * latency = real rate, so a fast backend still exceeds the limit. Spacing task *starts* by
 * `minIntervalMs` gives a hard rate ceiling (1000 / minIntervalMs starts per second) regardless of
 * latency; `concurrency` additionally caps how many run in flight.
 *
 * Defaults target ~33 starts/s (well under the 60 r/s nginx ceiling, leaving margin for the shared
 * `/v1` zone). Results preserve input order. `fn` must not throw — wrap per-item failures inside `fn`.
 */
export const mapRateLimited = async <T, R>(
  items: ReadonlyArray<T>,
  fn: (item: T, index: number) => Promise<R>,
  options?: { readonly concurrency?: number; readonly minIntervalMs?: number },
): Promise<ReadonlyArray<R>> => {
  const concurrency = options?.concurrency ?? 6;
  const minIntervalMs = options?.minIntervalMs ?? 30;

  const results = new Array<R>(items.length);
  let cursor = 0;
  let nextStartAt = 0;

  const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

  const worker = async (): Promise<void> => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;

      // Reserve this task's start slot atomically (no await between read and write → no race).
      const now = Date.now();
      const startAt = Math.max(now, nextStartAt);
      nextStartAt = startAt + minIntervalMs;

      const wait = startAt - now;
      if (wait > 0) {
        await sleep(wait);
      }

      results[index] = await fn(items[index], index);
    }
  };

  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return results;
};
